import { createFileRoute } from "@tanstack/react-router";
import type { Id } from "@convex/_generated/dataModel";
import { useLeague } from "@/features/leagues/hooks/useLeague";
import { useLeagueGames } from "@/features/leagues/games/hooks/useLeagueGames";
import { LeagueGamesTable } from "@/features/leagues/games/components/LeagueGamesTable";
import { PageContainer } from "@/shared/layout/PageContainer";
import { PageHeader } from "@/shared/layout/PageHeader";
import { useT } from "@/shared/i18n/LocaleContext";
import { EmptyState } from "@/shared/ui/EmptyState";
import { Skeleton } from "@/shared/ui/Skeleton";

export const Route = createFileRoute("/_authenticated/leagues/$leagueId/games/")({
  component: LeagueGamesPage,
});

function LeagueGamesPage() {
  const { leagueId } = Route.useParams();
  const typedLeagueId = leagueId as Id<"leagues">;
  const t = useT();
  const leagueData = useLeague(typedLeagueId);
  const season = leagueData?.league.season;
  const rows = useLeagueGames(season ?? -1);

  const isLoading = leagueData === undefined || rows === undefined;

  return (
    <PageContainer>
      <PageHeader
        {...(leagueData ? { eyebrow: leagueData.league.name } : {})}
        title={t("league.games.title")}
      />

      {isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          eyebrow={t("league.games.title")}
          title={t("league.games.emptyTitle")}
          description={t("league.games.empty")}
        />
      ) : (
        <LeagueGamesTable leagueId={typedLeagueId} rows={rows} />
      )}
    </PageContainer>
  );
}
